/**
 * 日本語の文言。型 `Messages` により en.ts の全キーが必須（漏れ・余計なキーは typecheck で落ちる）。
 *
 * 表記の決め事（開発者向けプロダクトの日本語）:
 *   - 説明文は です/ます調。ナビ・ボタン・見出しは短い名詞（概要・利用者・ランキング・APIキー…）。
 *   - 日本語の語と語の間に半角スペースを入れない。英数字との間も原則詰める（「APIキー」「v3を公開」）。
 *   - コード識別子・CLI コマンド・JSON キー・環境変数名は翻訳しない（{code} 等で差し込まれる）。
 *   - 複数形の区別は無いので other だけ書く（文字列で書いてもよい）。
 */
import type { Messages } from './index';
import { jaMoney } from './ja.money';
import { jaFnShare } from './ja.fnShare';
import { jaFnSecrets } from './ja.fnSecrets';
import { jaEnterKey } from './ja.enterKey';
import { jaUpload } from './ja.upload';
import { jaAccount } from './ja.account';
import { jaMail } from './ja.mail';
import { jaAppeals } from './ja.appeals';
import { jaOAuth } from './ja.oauth';
import { jaVerify } from './ja.verify';
import { jaHome } from './ja.home';
import { jaSlug } from './ja.slug';
import { jaProjectName } from './ja.projectName';
import { jaTransfer } from './ja.transfer';
import { jaMembers } from './ja.members';
import { jaLoginPasskey } from './ja.loginPasskey';
import { jaSecretKeys } from './ja.secretKeys';
import { jaMcpAuth } from './ja.mcpAuth';
import { jaCliAuth } from './ja.cliAuth';
import { jaCliDevice } from './ja.cliDevice';
import { jaOneAccount } from './ja.oneAccount';
import { jaConnect } from './ja.connect';
import { jaGuide } from './ja.guide';
import { jaConfirmAction } from './ja.confirmAction';
import { jaConfirmPlain } from './ja.confirmPlain';
import { jaInvite } from './ja.invite';
import { jaCoreFirst } from './ja.coreFirst';
import { jaConnectPics } from './ja.connectPics';
import { jaSelfAds } from './ja.selfAds';
import { jaPlayEarn } from './ja.playEarn';

export const ja: Messages = {
  // お金まわり（ja.money.ts）
  ...jaMoney,
  // Functions・X の共有の画面（ja.fnShare.ts・2026-09-28 Money の辞書から移した）
  ...jaFnShare,
  // Functions の画面の「API キーやパスワード」の欄（ja.fnSecrets.ts・2026-10-04 console/secret-input）
  ...jaFnSecrets,
  // キーを 1 つ入れるだけの小さな画面（ja.enterKey.ts・2026-10-04 creator/chat-secrets）
  ...jaEnterKey,
  // ゲームのファイルを入れるだけの小さな画面（ja.upload.ts・2026-10-04 creator/web-upload）
  ...jaUpload,
  // アカウントの設定（閉鎖）の画面（ja.account.ts）
  ...jaAccount,
  // メール配信の画面（ja.mail.ts）
  ...jaMail,
  // BAN の異議申し立ての欄（ja.appeals.ts）
  ...jaAppeals,
  // Google / Apple / GitHub のログイン・再認証・「ログイン方法」（ja.oauth.ts・2026-09-27 creator/dev-oauth-ui）
  ...jaOAuth,
  // メールの確認・未確認の案内・パスワードの決め直し（ja.verify.ts・2026-09-28 creator/email-unverified）
  ...jaVerify,
  // ログイン直後の画面（ホーム・ja.home.ts・2026-09-28 creator/home-redesign）
  ...jaHome,
  // プロジェクトのスラッグの変更・古いスラッグの転送（ja.slug.ts・2026-09-28 creator/slug-rename）
  ...jaSlug,
  // プロジェクトの名前の変更（ja.projectName.ts・2026-09-28 creator/project-rename・AGT-10）
  ...jaProjectName,
  // プロジェクトを人に渡す（ja.transfer.ts・2026-10-21 core/handoff-1a）
  ...jaTransfer,
  ...jaMembers,
  // ログイン用パスキー（ja.loginPasskey.ts・2026-09-28 creator/passkey-login）
  ...jaLoginPasskey,
  // パスワード再設定の完了・サーバーの鍵（ja.secretKeys.ts・2026-09-28 creator/dashboard-pass）
  ...jaSecretKeys,
  // MCP の接続の許可の画面（ja.mcpAuth.ts・2026-09-28 creator/mcp-dashboard-login）
  ...jaMcpAuth,
  // /cli/authorize（この端末の Kumo CLI を許可・ja.cliAuth.ts・2026-09-28 creator/connect）
  ...jaCliAuth,
  // /cli/device（デバイスコードでのログイン・ja.cliDevice.ts・2026-10-07 console/device-login）
  ...jaCliDevice,
  // 1 つの KUMODeck アカウント（ログインを account へ・Player / Creator・招待コード。ja.oneAccount.ts・2026-10-07 console/one-account）
  ...jaOneAccount,
  // /connect（ja.connect.ts・2026-10-07 console/connect-again）
  ...jaConnect,
  // /guide（使い方ガイド・ja.guide.ts・2026-10-07 console/guide-page）
  ...jaGuide,
  // /confirm/:id（AI エージェントの本番の変更をリンクで確認・ja.confirmAction.ts・2026-09-28 creator/confirm-link）
  ...jaConfirmAction,
  // 確認の画面の見える所（見出し・結果・ボタン・安全の 1 行。ja.confirmPlain.ts・2026-10-03 console/plain-confirm）
  ...jaConfirmPlain,
  ...jaInvite,
  // 芯を先に（ja.coreFirst.ts・2026-09-30 console/core-first）
  ...jaCoreFirst,
  // 「AIをつなぐ」の絵（ja.connectPics.ts・2026-10-19 design/visual-guides）
  ...jaConnectPics,
  // 自社の広告の枠（作り手が自分のゲームの広告を出す・ja.selfAds.ts・2026-10-22 money/self-serve-ads-dash）
  ...jaSelfAds,
  // 「初日から稼げる」の画面（ja.playEarn.ts・2026-10-26 money/earn-build）
  ...jaPlayEarn,
  // ------------------------------------------------------------ app / common
  'app.title': '{product}のダッシュボード',
  'app.starting': 'ダッシュボードを起動しています',
  'common.loading': '読み込み中',
  'common.loadingMore': '読み込み中…',
  'common.loadMore': 'さらに読み込む',
  'common.cancel': 'キャンセル',
  'common.working': '処理中…',
  'common.copy': 'コピー',
  'common.copied': 'コピーしました✓',
  'common.close': '閉じる',
  'common.retry': 'もう一度試す',
  'common.snippet': 'スニペット',
  'common.optional': '（任意）',
  'common.unnamed': '名前なし',
  'common.guest': 'ゲスト',
  'common.never': 'まだ使われていません',
  'common.language': '表示言語',
  'common.menu': 'メニュー',
  'common.logOut': 'ログアウト',
  'common.skip': '本文へスキップ',
  'common.sentenceSep': '',
  'common.brandSub': 'ダッシュボード',

  'time.justNow': '数秒前',
  'time.dur.s': '{s}秒',
  'time.dur.ms': '{m}分{s}秒',
  'time.dur.hm': '{h}時間{m}分',

  // ------------------------------------------------------------ env
  'env.development': 'テスト用',
  'env.production': '公開中',
  'env.inline.development': 'テスト用のアプリ',
  'env.inline.production': '公開中のアプリ',
  'env.tag.development': 'テスト用のアプリ',
  'env.tag.production': '公開中のアプリ',
  'env.band.development': 'テスト用のアプリを操作しています（{url}）。ここで何を変えても、公開中のアプリは変わりません。',
  'env.band.production': '公開中のアプリを操作しています（{url}）。ここで変えると、使っている人の画面もすぐに変わります。',
  'env.band.development_noUrl': 'テスト用のアプリを操作しています。ここで何を変えても、公開中のアプリは変わりません。',
  'env.band.production_noUrl': '公開中のアプリを操作しています。ここで変えると、使っている人の画面もすぐに変わります。',
  'env.chip.development': 'テスト用',
  'env.chip.production': '公開中',

  // ------------------------------------------------------------ errors
  'error.notAvailableTitle': '{what}はまだ利用できません',
  'error.notAvailableBody': 'この{product}サーバーは、この画面の機能にまだ対応していません。使えるようになると自動で表示されます（ダッシュボードの更新は不要です）。',
  'error.thisPanel': 'この画面',
  'error.connection': '接続できません',
  'error.generic': 'エラーが発生しました',
  'error.email_taken': 'このメールアドレスのアカウントはすでに存在します。ログインしてください。',
  'error.slug_taken': 'このURLの名前は、もうほかで使われています。別の名前にしてください。',
  'error.slug_reserved': 'その名前は使えません。www・admin・login などの名前や「kumo」を含む名前は、URLの名前にできません。',
  'error.invalid_request': '入力内容に誤りがあります（{detail}）',
  'error.unauthorized': 'ログインの期限が切れました。もう一度ログインしてください。',
  'error.invalidCredentials': 'メールアドレスまたはパスワードが正しくありません。',
  'error.forbidden': 'このアカウントでは、この操作はできません。',
  'error.not_found': '見つかりません。削除された可能性があります。',
  'error.payouts_not_configured': '受取口座の設定が完了していません。本番の決済を受け付ける前に、受取口座の登録を済ませてください。',
  'error.payments_not_configured': 'このサーバーでは決済が設定されていません。',
  'error.payment_provider_error': '決済サービスがエラーを返しました（{detail}）',
  'error.already_owned': 'この利用者はすでにこの商品を所有しています。',
  'error.not_refundable': 'この購入は返金できません（{detail}）',
  'error.rate_limited': '回数が多すぎます。{seconds}秒ほど待ってから、もう一度お試しください。',
  'error.network_error': '{product}に接続できません。ネットにつながっているか確かめてから、もう一度お試しください。',
  'error.route_missing': 'この機能は、まだ使えません。',
  'error.internal_error': '思わぬエラーが起きました（あなたの操作のせいではありません）。しばらくしてから、もう一度お試しください。',
  'error.http_error': 'うまくいきませんでした（{detail}）。しばらくしてから、もう一度お試しください。',
  // 独自ドメイン
  'error.domain_taken': 'このドメインは、同じプロジェクトのもう一方（Functionsかアプリの公開）ですでに使っています。そちらで外すか、別の名前にしてください。',
  'error.domain_limit': '確認待ちの独自ドメインがすでに{limit}個あります。それらのTXTレコードを置く（確認済みのドメインは数えません）か、要らないものを消してから、もう一度足してください。',
  'error.custom_domain_capacity': 'この{product}では今、独自ドメインをこれ以上追加できません。しばらくしてからお試しください。',
  'error.custom_domains_unavailable': 'この{product}のサーバーでは独自ドメインが設定されていません。',
  'error.production_only': '独自ドメインは、公開中のアプリだけで使えます。',
  'error.feature_unavailable': '{feature}は使えません。',
  'error.feature_disabled': '{feature}がOFFです。概要ページの「機能」でONにするか、AIに「{feature}をONにして」と頼んでください。',
  'error.deployment_not_ready': 'この版はまだ準備ができていない（アップロード中か、途中で止まった）ため公開できません。AIに「もう一度公開して」と頼んでください。',
  'error.deployment_failed': 'この版は失敗したか、期限が切れています。AIに「もう一度公開して」と頼んでください。',
  'error.functions_disabled': 'FunctionsがOFFです。先にONにしてください（Functionsの画面の「オンにする」か、AIに頼む）。',
  'error.functions_busy': 'Functionsを別に更新している途中です。少し待ってから、もう一度お試しください。',
  'error.functions_not_deployed': '先にFunctionsを公開してください（AIに「サーバーを公開して」と頼む）。',
  'error.durable_object_removed': 'この更新をすると、Functionsに保存したデータの一部（使わなくなった分）が消えます。何も変えていません。消してよいかを、AIに確かめてもらってください。',
  'error.publish_in_progress': 'ほかでも公開を送っている途中です。何も変わっていません。少し待ってから、もう一度お試しください。',
  'error.resource_limit': '{kind}は{limit}個までです（今{existing}個）。古いものは自動では消えません（名前を変えると新しく作られ、前のものは残ります）。今ある名前を使うか、使っていないものを消してから、もう一度公開してください。AIにこの文を伝えると直してくれます。',
  'error.share_images_disabled': 'このプロジェクトでは共有カードの画像がオフです。',
  'error.share_tags_disabled': 'このプロジェクトでは共有カードのタグがオフです。',
  'error.share_inAppBrowser_disabled': 'このプロジェクトではアプリ内ブラウザからの切り替えがオフです。',

  // ------------------------------------------------------------ auth
  'auth.sideTitle': '{product}のダッシュボード',
  'auth.sideBody': 'ここでは、AIエージェントをつなぎ、前払いの残高を入れ、プロジェクトと利用者・使った金額・売上を確かめられます。',
  'auth.sideBodyLaunch': 'ここでは、AIエージェントをつなぎ、前払いの残高を入れ、プロジェクトと利用者・使った金額を確かめられます。',
  'auth.signupTitle': 'アカウントを作成',
  'auth.loginTitle': '{product}にログイン',
  'auth.signupLead': 'ひとつのアカウントで、すべてのプロジェクトを管理できます。使った分だけ、前払いの残高から原価で差し引きます（無料枠はありません）。',
  // 2026-10-18 gtm/early-fixes（WHY は en.ts）
  'auth.signupLeadAd': 'ひとつのアカウントで、すべてのプロジェクトを管理できます。使った分を原価で、前払いの残高から払います。{amount}のクレジットがその最初の分です。',
  'auth.signupLeadAdCredit': 'ひとつのアカウントで、すべてのプロジェクトを管理できます。使った分を原価で、前払いの残高から払います。招待クレジットがその最初の分です。',
  'auth.loginLead': 'ログインしてプロジェクトを管理します。',
  // 2026-10-31 core/login-from-ai（WHY は en.ts）
  'auth.loginLeadAiConnect': 'ログインしてAIアプリをつなぎます。',
  'auth.name': '名前',
  'auth.email': 'メールアドレス',
  'auth.password': 'パスワード',
  'auth.passwordHint': '{min}文字以上',
  'auth.passwordTooShort': '{min}文字以上にしてください。このアカウントで、プロジェクト・利用者のデータ・公開を管理します。',
  'auth.busy': 'お待ちください…',
  // /early の「Continue with GitHub / Google」から来て、プロバイダへ移る間だけ出す 1 行（2026-10-22 site/early-inline-signup・AuthPage の handoff）
  'auth.handoff': '{provider} を開いています…',
  'auth.createAccount': 'アカウントを作成',
  'auth.logIn': 'ログイン',
  'auth.haveAccount': 'アカウントをお持ちの方は{link}',
  'auth.newHere': '{product}は初めてですか？{link}',
  'auth.createAnAccount': 'アカウントを作成',
  // 2026-09-28 creator/login-redesign: パスワードの表示の切り替え・前回の方法の印・この端末に保存・パスキーでのログイン（置き場）
  'auth.showPassword': 'パスワードを表示',
  'auth.hidePassword': 'パスワードを隠す',
  'auth.lastUsed': '前回使用',
  'auth.rememberDevice': 'この端末にメールアドレスとログイン方法を保存する',
  'auth.passkeyLogin': 'パスキーでログイン',
  'auth.passkeyFailedLaunch': 'このパスキーではログインできませんでした。アカウントの「ログイン方法」で追加したログイン用パスキーを使うか、別の方法でログインしてください。',
  'auth.passkeyFailed': 'このパスキーではログインできませんでした。アカウントの「ログイン方法」で追加したログイン用パスキーを使うか（出金用のパスキーではログインできません）、別の方法でログインしてください。',

  // ------------------------------------------------------------ projects
  'projects.eyebrow': 'あなたのプロジェクト',
  'projects.title': 'プロジェクト',
  'projects.new': '＋新規プロジェクト',
  'projects.emptyTitle': 'プロジェクトはまだありません',
  'projects.emptyBody': 'プロジェクトは1つのアプリ（ゲームも）です。公開中のアプリと、試すためのテスト用のアプリがあり、URL・利用者・キーはそれぞれ別です。',
  'projects.createFirst': '最初のプロジェクトを作成',
  'projects.created': '{ago}に作成',
  'projects.createdEyebrow': 'プロジェクトを作成しました',
  'projects.savedOpen': 'キーを保存しました — プロジェクトを開く →',
  'projects.form.title': '新規プロジェクト',
  'projects.form.name': 'プロジェクト名',
  'projects.form.namePlaceholder': 'Booking App',
  'projects.form.slug': 'URLの名前',
  'projects.form.slugNote': '（任意・あとから変えられます（1日に1回））',
  'projects.form.slugHint': 'アプリのURLに入る名前です。たとえば「neko-jump」と入れると、URLの「neko-jump」の部分になります。空欄ならプロジェクト名から自動で決まります。',
  'projects.form.slugInvalid': 'URLの名前は3〜58文字で、英小文字・数字・ハイフンだけが使えます。',
  'projects.form.slugSuggestions': 'いま空いている名前です。押すと欄に入ります:',
  'projects.form.creating': '作成中…',
  'projects.form.submit': 'プロジェクトを作成',

  // ------------------------------------------------------------ keys (reveal + page)
  'key.kind.publishable': 'アプリに書き込むキー（pk_）',
  'key.kind.secret': 'サーバーだけで使うキー（sk_）',
  'key.secretNote': 'これがあると設定の変更や公開ができてしまいます。人に見せず、AIとの会話にも貼らないでください',
  'key.publishableNote': 'アプリのコードに入るので、使う人にも見えます。見えても大丈夫なキーです',
  'key.secretWarnTitle': 'サーバーだけで使うキー（sk_で始まる）は、今しか表示されません。',
  'key.secretWarnBody': '{product}にも控えは残りません。今すぐパスワード管理アプリなどにコピーしてください。なくしたときは、そのキーを使えなくして、新しく作ります。',
  'key.quickstart': 'クイックスタート',
  'key.quickstartLead': 'アプリのHTMLに貼り付けてください。テスト用のアプリのキーなので、公開中のアプリには影響しません。',
  'key.quickstartComment': '利用者をサインイン',
  'keys.title': 'キー（pk_ / sk_）',
  'keys.storedHide': '保存しました — 閉じる',
  'keys.createTitle': '{env}のキーを作成',
  'keys.kindLabel': 'キーの種類',
  'keys.label': 'ラベル',
  'keys.labelSecret': 'ラベル（例: GitHub Actions）',
  'keys.labelPublishable': 'ラベル（例: itch.io版）',
  'keys.creating': '作成中…',
  'keys.create': 'キーを作成',
  'keys.help': '{publishable}はアプリのコードに入るので、使う人にも見えます。見えても大丈夫なキーで、利用者としてできることしかできません。{secret}があると設定の変更や公開ができてしまいます。人に見せず、AIとの会話にも貼らないでください。',
  'keys.helpPublishable': 'アプリに書き込むキー（pk_で始まる）',
  'keys.helpSecret': 'サーバーだけで使うキー（sk_で始まる）',
  'keys.listTitle': 'キー一覧',
  'keys.showRevoked': '使えなくしたキー{count}件を表示',
  'keys.col.key': 'キー',
  'keys.col.type': '種類',
  'keys.col.created': '作った日',
  'keys.col.lastUsed': '最後に使われた日',
  'keys.col.actions': '操作',
  'keys.revokedAgo': '{ago}に使えなくした',
  'keys.revoke': '使えなくする',
  'keys.revokeTitle': '{key}を使えなくしますか？',
  'keys.revokeTitleGeneric': 'キーを使えなくしますか？',
  'keys.revokeConfirm': 'キーを使えなくする',
  'keys.revokeBody': 'このキーはすぐに使えなくなります。元に戻せません。',
  'keys.revokeBodyPublishable': 'このキーはすぐに使えなくなります。このキーを入れたアプリは、新しいキーに入れ替えて公開し直すまで動きません。元に戻せません。',

  // ------------------------------------------------------------ shell
  'shell.navLabel': 'プロジェクトのナビゲーション',
  'shell.project': 'プロジェクト',
  'shell.environment': '操作するアプリ',
  'nav.group.manage': '管理',
  'nav.overview': '概要',
  'nav.players': '利用者',
  'nav.leaderboards': 'ランキング',
  'nav.multiplayer': 'マルチプレイ',
  'nav.config': '設定ファイル',
  'nav.hosting': 'ホスティング',
  'nav.payments': '決済',
  'nav.keys': 'キー（pk_ / sk_）',
  'nav.audit': '操作の記録',

  // ------------------------------------------------------------ features（2026-09-27 task/toggles）
  'features.title': '機能',
  'features.count': '{total}個中{on}個がON',
  'features.body': '機能はONにするまですべてOFFです。OFFの機能は誰からの呼び出しも断るので、使っていない機能で利用料を増やされることはありません。ここで切り替えるか、AIに「セーブをONにして」のように頼みます（もとは手元のkumo.config.json。自分で打つなら{cmd}のあと{push}）。',
  'features.none': 'まだ何もONになっていません（ゲストのログインと設定の読み出しだけが使えます）。',
  'features.on': 'ON',
  'features.off': 'OFF',
  'features.turnOn': 'ONにする',
  'features.turnOff': 'OFFにする',
  'features.setUpSignIn': '設定する',
  'features.openFunctions': 'Functionsを開く',
  'features.confirmOnTitle': '{env}で{name}をONにしますか？',
  'features.confirmOffTitle': '{env}で{name}をOFFにしますか？',
  'features.confirmOnBody': '{env}を使っている人に、数秒で反映されます。',
  'features.confirmOffBody': '数秒で、OFFにした機能は{env}で使えなくなり、アプリのその部分が動かなくなります。',
  'features.confirmOn': '{env}でONにする',
  'features.confirmStop': 'サイトを止める',
  'features.stopServing': 'サイトは公開中です: {url}。{features} を OFF にすると、すぐに止まります。まだ何も変えていません。止めるなら「サイトを止める」を押してください。',
  'features.stopServingNoUrl': 'サイトは公開中です。{features} を OFF にすると、すぐに止まります。まだ何も変えていません。止めるなら「サイトを止める」を押してください。',
  'features.confirmOff': '{env}でOFFにする',
  'features.alsoOn': 'ほかの行はこの機能に必要なので、一緒にONになります。',
  'features.alsoOff': 'ほかの行はこの機能を使うので、一緒にOFFになります。',
  'features.localNote': '手元の{file}は変わりません。手元にも同じ変更を入れないと、次の{push}で元に戻ります。',
  'features.typeSlug': '公開中のアプリです。確認のため{slug}と入力してください。',
  'features.stale': 'このページを開いた後にconfigが変わりました（いまはv{version}）。一覧を確かめてから、もう一度お試しください。',
  'features.doneTitle': '反映しました（config v{version}）。',
  'features.doneSame': '変更はありません（すでにその状態でした）。',
  'features.pendingTitle': 'ここで変えた機能: {names}',
  'features.pendingBody': '手元のファイルにまだ入っていなければ、次のpushで元に戻ります。',
  'features.revertedTitle': 'その後のpushで元に戻りました: {names}',
  'features.revertedBody': 'ここで変えましたが、その後に手元のファイルからpushした内容には入っていませんでした。',
  'features.localFix': '手元の{file}にも同じ変更を入れてください:',
  'features.localManual': 'ファイルの{path}を{value}にします。',
  'features.localManualThen': 'そのあと{cmd}を実行します。',
  'features.dismiss': '手元に入れた',
  'feature.emailLogin': 'メールでのログイン（登録・確認・パスワード再設定）',
  'feature.googleLogin': 'Googleでログイン',
  'feature.discordLogin': 'Discordでログイン',
  'feature.appleLogin': 'Appleでログイン',
  'feature.xLogin': 'Xでログイン',
  'feature.saves': 'クラウドセーブ',
  'feature.stats': 'ステータス（利用者の値の送信・読み出し）',
  'feature.leaderboards': 'ランキング',
  'feature.achievements': '実績・ミッション',
  'feature.inventory': '通貨・アイテム',
  'feature.exchanges': 'アプリ内ショップ・交換',
  'feature.multiplayer': 'マルチプレイ（ルーム・マッチング）',
  'feature.p2p': 'P2P のマルチプレイ（WebRTC・判定役なし・既定は TURN を中継）',
  // リアルタイムの部屋（features.realtimeChannels・2026-09-27）。「チャット機能」とは書かない（MASTER-PLAN §1c: フラットな土台として出す）
  'feature.realtimeChannels': 'リアルタイムの部屋（名前の付いた部屋でメッセージを即座に届ける・中身は保存しない）',
  // 音声通話（features.voice・requires realtimeChannels・2026-09-27 creator/voice-docs）。「ボイチャ機能」とは書かない（MASTER-PLAN §1c）。
  // 「録音しない」を名前に入れる WHY: ON にする人が真っ先に気にするのがプライバシーで、Kumo は録音しない（realtime/README.md「音声通話」）
  // 人数（2026-09-28 creator/voice-sfu-docs）: Core の voice-sfu で 9 人以上は中継サーバー（SFU）・上限 100 になったので「8 人まで・P2P」から直した
  'feature.voice': 'リアルタイムの部屋の音声通話（100人まで・録音しない）',
  'feature.payments': 'アプリ内課金',
  'feature.ads': 'リワード動画の広告',
  // 表示の広告のカード（2026-10-19 creator/ads-open）。英語の側の注記と同じ決まり
  'displayAds.title': 'バナー広告',
  'displayAds.body': '{product}が公開中のページに広告を入れます（コードは不要）。次に開かれたときから出て、OFFにすると次に開かれたときから消えます。',
  'displayAds.name.banner': 'バナー',
  'displayAds.name.socialBar': 'Social Bar',
  // en と同じ（2 つの違いを 1〜2 行・Social Bar の事実・良い点 / 気になる点・決めるのは作り手。「稼げる」「収益」は書かない）
  'displayAds.kind.banner': 'ページの下にとどまる帯（スマホは320×50、広い画面は728×90）。いつも同じ形で、控えめです。',
  'displayAds.kind.socialBar': 'アプリの画面の上に重なって出る広告。形はAdsterraが選びます。バナーより目立ちます。',
  'displayAds.pic.label': 'どこに出るか',
  'displayAds.pic.banner': 'バナー: 下の帯',
  'displayAds.pic.socialBar': 'Social Bar: 画面の上に重なる',
  'displayAds.pic.alt.banner': 'アプリの画面のスマホ。下に広告の帯が固定',
  'displayAds.pic.alt.socialBar': 'アプリの画面のスマホ。上にお知らせのような広告が重なり、タブの文字が「(1) New Message!」に変わる',
  'displayAds.sb.more': 'Social Barはどんな広告か',
  'displayAds.sb.over': 'アプリの画面の上に重なって出ます（専用の帯の中ではありません）。',
  'displayAds.sb.forms': '形はそのつどAdsterraが選びます。通知のような小さな広告・ウィジェット・アイコン・画面全体を覆う広告のどれかです。形は選べません。',
  'displayAds.sb.title': 'ブラウザのタブの文字を「(1) New Message!」のように変えることがあります。',
  'displayAds.sb.noReward': '見た人に報酬はありません。リワード広告ではなく、出たことはアプリに知らされません。',
  'displayAds.sb.goodLabel': '良い点',
  'displayAds.sb.good': '目立つ分、バナーより入るお金が多くなりやすい広告です。決まった額はなく、そうならないこともあります。',
  'displayAds.sb.considerLabel': '気になる点',
  'displayAds.sb.consider': '使っている最中にアプリの一部に重なることがあります。タブの文字がメッセージの通知のように見えることもあります。',
  'displayAds.sb.yours': '決めるのはあなたです。バナーと一緒でも、Social Barだけでも、使わなくてもかまいません。いつでもOFFにできます。',
  'displayAds.onTitle': 'バナー広告がONです。',
  'displayAds.offTitle': '{name}はOFFです。',
  'displayAds.share': 'あなたのアプリの広告についてAdsterraから実際に入ったお金の100%があなたの分です。',
  'displayAds.estimate': '{product}のアプリはみな同じ広告の枠を使うため、あなたの分は概算です。枠のその日の支払いを、アプリごとに広告が表示された回数の比で分けます。',
  'displayAds.when': 'Adsterraの確定（数日）と60日の確認期間の後に残高に入ります。',
  'displayAds.where': '広告が出るのは{host}の公開中のページだけです。テスト版・独自ドメインには出ません。遊ぶ人の年齢層がkids・mixedのときも出ません。ヨーロッパの訪問者には、広告を読み込む前に許可を聞きます。',
  'displayAds.devOnly': '広告は公開版にだけ出ます。ONにするには{env}に切り替えてください。',
  'displayAds.needsHosting': '先にホスティングをONにしてください（広告は{product}が配信するページに入ります）。',
  'displayAds.confirmOnTitle': '{name}をONにしますか？',
  'displayAds.confirmOffTitle': '{name}をOFFにしますか？',
  'displayAds.confirmOnBody': '次に開かれたときから、公開中のページに広告が出ます。',
  'displayAds.confirmOffBody': '次に開かれたときから、公開中のページの広告が消えます。',
  // KUMODeck 自身の広告だけが出る作り手の版（en.ts と同じ・2026-10-27 console/earn-ads-card）
  'displayAds.title.kumo': '{product}の広告',
  'displayAds.onTitle.kumo': '{product}の広告がONです。',
  'displayAds.house.body': 'いま出る広告は{product}の短い宣伝だけです。{product}が公開中のページに入れます（コードは不要）。',
  'displayAds.kind.socialBarHouse': 'いまは出ません（{product}の広告だけが出ています）。ここでOFFにできます。',
  'feature.mail': '利用者へのメール',
  'feature.friends': 'フレンド',
  'feature.presence': 'フレンドのオンライン状態',
  'feature.chat': 'チャット',
  'feature.guilds': 'ギルド',
  'feature.events': 'イベントカレンダー',
  'feature.loginBonuses': 'ログインボーナス',
  'feature.gifts': 'プレゼントボックス（利用者への配布）',
  'feature.serials': 'シリアルコード',
  'feature.gachas': '無償ガチャ',
  'feature.moderation': '通報・制裁',
  'feature.statsVerification': 'ステータスの検証（値の範囲・自分のサーバーでの確認）',
  'feature.hosting': 'ホスティング（アプリの公開）',
  'feature.serverRendering': 'サーバーで画面を作るアプリ（ホスティングが必要）',
  'feature.customDomains': '公開したアプリの独自ドメイン（app.example.com・公開中のアプリだけ）',
  'feature.shareImages': '共有カードの画像',
  'feature.shareTags': '公開ページの共有カードのタグ',
  'feature.shareLinks': '共有リンク・挑戦状',
  'feature.shareTracking': '投稿ごとの訪問の記録',
  'feature.inAppBrowser': 'アプリ内ブラウザから本来のブラウザで開く',
  'feature.ai': 'サーバーからのAI（サーバーだけで使うキー）',
  'feature.functions': 'Functions（自分のサーバーのコードとDB）',
  'feature.network': 'プロジェクトをまたぐ利用者のアカウント（まだ使いません）',
  'featureHint.title': '{env}でOFF: {names}',
  'featureHint.body': 'ONにするまで、ここには何も表示されません（OFFの機能はアプリから呼んでも断られます）。手元のファイルでONにして反映するか、{link}で切り替えてください。',
  'featureHint.link': '概要ページの「機能」',
  'projects.emptyCli': 'プロジェクトのフォルダからも作れます: {cmd}',
  'mp.noRoomsStarter': 'これから作るなら、{cmd}で2人対戦の動く雛形ができます。',
  'keys.emptyTitle': '{env}に有効なキーがありません',
  'keys.emptyBody': 'アプリにはキーが必要です。上で作るか、AIに「キーを用意して」と頼んでください（自分で打つなら{cmd}）。',
  'share.turnOnCmd': 'Xのカード・共有リンク・記録をまとめてONにして反映するには: {cmd}',
  'share.emptyCli': '自分でXに投稿するなら、{cmd}で記録つきのリンクを作れます（AIエージェントからは{mcp}）。',
  'fn.noDeploysCmd': 'AIに「サーバーを公開して」と頼むか、{cmd}で公開します。',
  'key.quickstartNext': '使う機能を先にONに: {cliName} features on saves',
  // ------------------------------------------------------------ overview
  'overview.title': '概要',
  'overview.keyNumbers': '主な数値',
  'overview.players': '利用者',
  'overview.banned': 'BAN中{count}人',
  'overview.allTime': '累計',
  'overview.active24h': 'アクティブ・24時間',
  'overview.active24hHint': '直近1日に使った',
  'overview.new24h': '新規・24時間',
  'overview.new7d': '7日間で{count}人',
  'overview.config': '設定ファイル',
  'overview.pushedAgo': '{ago}に反映',
  'overview.nothingPushed': '未反映',
  'overview.apiKeys': 'キー（pk_ / sk_）',
  'overview.revoked': '使えなくしたキー{count}件',
  'overview.active': '有効',
  'overview.dauTitle': 'デイリーアクティブ利用者',
  'overview.dauRange': '直近14日・UTC',
  'overview.dauLabel': 'デイリーアクティブ利用者（直近14日）',
  'overview.noPlayersTitle': '利用者はまだいません',
  'overview.noPlayersBody': 'アプリにSDKを組み込んでゲストとしてサインインすると、数秒で最初の利用者がここに表示されます。',
  'overview.nextSteps': '次のステップ',
  'overview.step2Title': '最初の利用者をサインインさせる。',
  'overview.step2Body': '作ったときにコピーしたアプリに書き込むキー（pk_で始まる・「キー（pk_ / sk_）」の画面でも作れます）を使って、アプリにSDKを追加します。',
  'overview.step3Title': '公開する。',
  'overview.step3Body': '{cmd}で公開できます。{link}から、いつでもすぐに前の状態に戻せます。',
  'overview.step3Link': 'ホスティング',

  // ------------------------------------------------------------ chart
  'chart.total': '合計{value}。',

  // ------------------------------------------------------------ players
  'players.title': '利用者',
  'players.search': '利用者を検索',
  'players.searchPlaceholder': '名前・メール・利用者IDで検索',
  'players.noMatchTitle': '「{q}」に一致する利用者はいません',
  'players.noMatchBody': '利用者IDは先頭一致、表示名とメールアドレスは部分一致で検索します。',
  'players.emptyTitle': '{env}の利用者はまだいません',
  'players.emptyBody': 'アプリが{prefix}キーで{guest}を呼ぶとここに表示されます（ゲストのログインは常にONです）。',
  'players.col.player': '利用者',
  'players.col.login': 'ログイン',
  'players.col.joined': '登録',
  'players.col.lastSeen': '最終アクセス',
  'players.col.status': '状態',
  'players.guestPill': 'ゲスト',
  'players.banned': 'BAN中',
  'players.active': '有効',

  // ------------------------------------------------------------ player drawer
  'player.fallbackTitle': '利用者',
  'player.bannedAgo': '{ago}にBAN',
  'player.provider.guest': 'ゲスト',
  'player.provider.email': 'メール',
  'player.noReason': '理由は記録されていません。',
  'player.id': '利用者ID',
  'player.account': 'アカウント',
  'player.guestAccount': 'ゲスト（この端末のみ）',
  'player.linkedAccount': '連携済みアカウント',
  'player.joined': '登録',
  'player.lastSeen': '最終アクセス',
  'player.sessions': 'ログイン中の端末',
  'player.signIn': 'サインイン方法',
  'player.stats': 'ステータス',
  'player.noStats': 'ステータスの記録はありません。',
  'player.inventory': '所持品',
  'player.noInventory': '所持品はありません。',
  'player.achievements': '実績',
  'player.noAchievements': '解除した実績はありません。',
  'player.unban': 'BANを解除',
  'player.ban': '利用者をBAN',
  'player.thisPlayer': 'この利用者',
  'player.banTitle': '{name}をBANしますか？',
  'player.unbanTitle': '{name}のBANを解除しますか？',
  'player.unbanConfirm': 'BANを解除',
  'player.banBody':
    '{env}のすべての端末からログアウトさせ、新しいログインを止めます。接続中のマルチプレイはすぐに切れます（空いた席はほかの人に開きます）。そのほかの操作も、15分以内に使えなくなります。',
  'player.reason': '理由',
  'player.reasonNote': '（操作の記録に記録されます）',
  'player.reasonPlaceholder': '例: マルチプレイでスピードハック',
  'player.unbanBody': '既存のアカウントで再びサインインできるようになります。進行状況は削除されていません。',
  'player.privacy': 'データとプライバシー',
  'player.privacyNote': 'この利用者からのデータ請求（GDPR、App Store / Google Play のアカウント削除）に対応するための操作です。どちらも操作の記録に記録されます。',
  'player.exportData': 'データをエクスポート',
  'player.exporting': 'エクスポート中…',
  'player.exported': '{file}を保存しました。',
  'player.delete': '利用者を削除',
  'player.deleteTitle': '{name}を完全に削除しますか？',
  'player.deleteBody':
    '{env}のアカウントを削除します: サインイン方法（メールアドレス）、セーブ、stats など、この利用者の保存データすべて。すべての端末でサインアウトされ、接続中のマルチプレイも切断されます。元に戻せません。本人からデータのコピーを求められている場合は、先にエクスポートしてください。',
  'player.deletePurchases': '購入記録は会計のために残りますが、誰の購入かは分からなくなります。',
  'player.deleteTypeId': '確認のため{id}と入力してください',
  'player.deleteConfirm': '完全に削除',
  'player.deleted': '利用者を削除しました。',

  // ------------------------------------------------------------ config
  'config.title': '設定ファイル',
  'config.liveVersion': '公開中のv{version}',
  'config.neverPushed': '未反映',
  'config.download': '{file}をダウンロード',
  'config.nothingPushedTitle': '{env}にはまだ何も反映されていません。',
  'config.nothingPushedBody': 'これはひな形です。ここで編集するか、リポジトリで管理して{cmd}を実行してください。',
  'config.unsaved': '● 未反映の変更あり',
  'config.inSync': '反映済み',
  'config.editorLabel': '設定ファイルのJSON',
  'config.pushing': '反映中…',
  'config.push': '検証して{env}に反映',
  'config.tabHint': 'Tabキーでインデントします。エディタから出るには、Escを押してからTabを押してください。',
  'config.pushed': 'v{version}として反映しました。5秒以内にアプリに反映されます。',
  'config.noChanges': '変更はありません（v{version}のままです）。',
  'config.invalidJson': 'JSONの形式が正しくありません',
  'config.invalidJsonLine': '{line}行目のJSONの形式が正しくありません',
  'config.goToLine': 'その行へ移動',
  'config.issues': '検証エラーが{count}件あります。反映は行われていません',
  'config.root': '（ルート）',
  'config.lineShort': '{line}行',
  'config.liveNote': '公開中のバージョンは{version}です。反映した内容はすべて保存されます。戻すときは古いファイルをもう一度反映してください。',
  'config.prodTitle': '公開中のアプリに反映しますか？',
  'config.prodConfirm': '公開中のアプリに反映',
  'config.prodBody': '使用中の利用者にも数秒で新しい定義が届きます。価格・報酬も含まれます。',

  // ------------------------------------------------------------ leaderboards / achievements
  'lb.title': 'ランキングと実績',
  'lb.noBoardsTitle': 'ランキングが定義されていません',
  'lb.noBoardsBody': '{file}にステータスとランキングを追加して{cmd}を実行するか、{link}で編集してください。',
  'lb.configLink': '設定ファイル',
  'lb.tabs': 'ランキング',
  'lb.stat': 'ステータス: {stat}',
  'lb.lowestWins': '小さいほど上位',
  'lb.highestWins': '大きいほど上位',
  'lb.reset.daily': '毎日リセット',
  'lb.reset.weekly': '毎週リセット',
  'lb.reset.monthly': '毎月リセット',
  'lb.reset.never': 'リセットなし',
  'lb.reset.other': 'リセット: {reset}',
  'lb.season': 'シーズン',
  'lb.thisSeason': '今シーズン',
  'lb.lastSeason': '前シーズン',
  'lb.noScoresTitle': 'スコアはまだありません',
  'lb.noScoresBody': '利用者が期間{period}に{call}を送るとスコアが表示されます。',
  'lb.ranked': 'ランクイン{count}人・期間: {period}',
  'lb.what': 'ランキング',
  'ach.title': '実績',
  'ach.eligible': '対象の利用者{count}人',
  'ach.emptyTitle': '実績が定義されていません',
  'ach.emptyBody': '{condition}を持つ実績はステータスから自動で解除されます。持たない実績は、アプリから{call}を呼んで解除します。',
  'ach.hidden': '非公開',
  'ach.mission.daily': 'デイリーミッション',
  'ach.mission.weekly': 'ウィークリーミッション',
  'ach.mission.monthly': 'マンスリーミッション',
  'ach.mission.other': '{reset}ミッション',
  'ach.manual': '手動で解除',
  'ach.meter': '利用者の{pct}%が解除',
  'ach.unlockedBy': '{n}人・{pct}%',

  // ------------------------------------------------------------ multiplayer
  'mp.title': 'マルチプレイ',
  'mp.live': 'ライブ・5秒ごとに更新',
  'mp.liveRooms': '稼働中のルーム',
  'mp.playersInRooms': 'ルーム内の利用者',
  'mp.inMatchmaking': 'マッチング待ち',
  'mp.rooms24h': 'ルーム数・24時間',
  'mp.avg': '平均{dur}',
  'mp.byMode': 'モード別',
  'mp.noRoomsTitle': '現在開いているルームはありません',
  'mp.noRoomsBody': '設定ファイルの{key}にモードを定義し、2つのブラウザタブから{call}を呼ぶとルームが表示されます。',
  'mp.col.mode': 'モード',
  'mp.col.rooms': 'ルーム',
  'mp.col.players': '利用者',
  'mp.col.queued': '待機中',
  'mp.recent': '最近終了したルーム',
  'mp.noRecent': '終了したルームはまだありません。',
  'mp.col.ended': '終了',
  'mp.col.length': '時間',
  'mp.col.peak': '最大／延べ人数',
  'mp.col.how': '経緯',
  'mp.private': 'プライベート',

  // ------------------------------------------------------------ hosting
  'hosting.title': 'ホスティング',
  'hosting.liveUrl': '公開URL',
  'hosting.byDeveloper': 'ログインしたあなた',
  'hosting.byKey': 'サーバーだけで使うキー（sk_）',
  'hosting.noUrl': 'このサーバーにはアプリの公開URLがまだありません。',
  'hosting.liveSince': '{ago}から公開中',
  'hosting.files': '{count}ファイル',
  'hosting.nothingLive': '公開中のものはありません',
  'hosting.emptyTitle': 'まだ公開していません',
  'hosting.deployments': '公開の履歴',
  'hosting.livePill': '公開中',
  'hosting.noMessage': 'メッセージなし',
  'hosting.rollBack': 'この版に戻す',
  'hosting.makeLive': '公開する',
  'hosting.nowLive': 'v{version}を公開しました。',
  'hosting.rollbackUnavailable': 'このサーバーではロールバックをまだ利用できません。',
  'hosting.confirmTitle': '{env}でv{version}を公開しますか？',
  'hosting.confirmLabel': 'v{version}に切り替え',
  'hosting.confirmBody': 'これ以降にアプリを読み込む利用者にはv{version}が配信されます。切り替えは即時で、いつでも元に戻せます。',
  // 独自ドメイン
  'domains.title': '独自ドメイン',
  'domains.lead': '自分のドメイン（例: app.example.com）でアプリを配信できます。利用者・共有リンク・Xのカードもそのドメインになり、{product}のURLは見えません。証明書は{product}が用意し、あなたはDNSのレコードを置くだけです。',
  'domains.devNote': '独自ドメインは公開中のアプリで使います。テスト用のアプリは今のURLのままです。追加するには、「操作するアプリ」で「公開中」を選んでください。',
  'domains.none': '独自ドメインはまだありません。',
  'domains.hostname': 'ドメイン',
  'domains.placeholder': 'play.mygame.com',
  'domains.add': 'ドメインを追加',
  'domains.added': '{hostname}を追加しました。ドメインを管理しているところに下のDNSレコード2行（CNAMEとTXT）を置いてから「今すぐ確認」を押してください。',
  'domains.status.pending': 'DNS待ち',
  'domains.status.active': '公開中',
  'domains.status.failed': '失敗',
  'domains.dnsLead': 'ドメインを管理しているところ（買ったレジストラやDNSの会社）に、次の2行を同時に追加してください。CNAMEは名前を{product}に向け、TXTはドメインの持ち主であることの証明です。{product}は1時間ごとに確認し、見つかると配信を始めます（数分〜数時間）。状態と「持ち主の確認」の表示で進み具合が分かります。',
  'domains.verified': '持ち主の確認済み',
  'domains.unverified': '持ち主の確認待ち',
  'domains.verifiedAt': '持ち主のTXTを最後に確認した日時: {at}',
  'domains.dnsKeep': 'DNSレコード（置いたままにしてください）',
  'domains.txtKeep': '公開中になった後もTXTは消さないでください。{product}は毎日確認し直し、TXTが無くなるとそのドメインでの配信を止めます。',
  'domains.parentTxt': '親のドメインに1つ（{name}）置けば、その下の名前はすべて通ります。ほかのサブドメインにTXTを置き直す必要はありません。',
  'domains.dnsLabel': 'DNSレコード',
  'domains.apexNote': 'これはルートのドメインです。DNSの会社によってはここにCNAMEを置けません。ALIAS / ANAME / CNAMEフラット化を使うか、play.<あなたのドメイン>のようなサブドメインにしてください。',
  'domains.check': '今すぐ確認',
  'domains.checked': '{hostname}を確認しました: {status}。',
  'domains.open': '開く',
  'domains.remove': '削除',
  'domains.cost': '{product}で自分のドメインを使うと、1ドメインにつき月{price}（証明書とそのドメインでの配信の分）を前払い残高から日割りで引きます。ドメインそのものの代金は、買ったところに払います。使わないものは消してください。',
  'domains.costUnpriced': '{product}で自分のドメインを使うと、1ドメインにつき月額（証明書とそのドメインでの配信の分）を前払い残高から日割りで引きます。ドメインそのものの代金は、買ったところに払います。使わないものは消してください。',
  'domains.cli': 'ターミナルから: {cmd}',
  'domains.removeTitle': '{hostname}を削除しますか？',
  'domains.removeBody': '{hostname}での配信はすぐに止まり、そのリンクは開けなくなります。アプリは{product}のURLで引き続き配信されます。このドメインは他の人が登録できるようになります。',
  'domains.removeType': '確認のため{hostname}と入力してください。',
  'domains.removeLabel': 'ドメインを削除',
  'domains.removed': '{hostname}を削除しました。',
  'domains.path': 'パス（任意）',
  'domains.pathPlaceholder': '/race',
  'domains.pathHint': '空のままならドメイン全体を使います。1つのドメインを自分の複数のアプリで分けられます（example.com/race、example.com/dash のようにパスごと）。パスの下に出すアプリでは、ファイルやリンクのパスを「/」で始めずに書いてください（/images/a.png ではなく images/a.png）。',
  'domains.willShow': 'アプリのURL: {url}',
  'domains.addedLive': '{hostname}を追加し、{url}で公開しました。あなたの別のアプリがこのドメインをすでに使っているので、DNSレコードは要りません。',
  'domains.appUrl': 'このアプリ: {url}',
  'domains.otherPaths': 'このドメインの他のアプリ: {paths}',
  'domains.warningsTitle': '{hostname}では、アプリの次の所が動かないかもしれません（{product}はファイルを書き換えません。説明は英語です）:',
  'domains.removeBodyPath': '{url}でのこのアプリの配信はすぐに止まり、そのリンクは開けなくなります。アプリは{product}のURLで引き続き配信されます。ドメインは、そこにある他のアプリ（{paths}）のために残ります。',
  'domains.removeLabelPath': 'このアプリをドメインから外す',
  'deploy.status.pending': '準備中',
  'deploy.status.uploading': 'アップロード中',
  'deploy.status.ready': '準備完了',
  'deploy.status.failed': '失敗',

  // ------------------------------------------------------------ payments
  'pay.title': '決済',
  'pay.notConfigured': '未設定',
  'pay.mode.fake': '疑似決済',
  'pay.mode.test': 'Stripeテストモード',
  'pay.mode.live': 'Stripe本番',
  'pay.noSalesTitle': '{env}の売上はまだありません',
  'pay.noSalesBody': '{link}に{products}を定義し、アプリ内のボタンから{call}を呼んでください。支払いが確定した購入はすぐにここに表示されます。',
  'pay.configLink': '設定ファイル',
  'pay.perCurrency': '合計は通貨ごとに集計し、換算はしません。',
  'pay.currency': '通貨',
  'pay.revenueIn': '{currency}の売上',
  'pay.gross': '総売上',
  'pay.paidPurchases': '支払い済み{count}件',
  'pay.refunds': '返金',
  'pay.inclDisputed': 'うち異議申し立て{amount}',
  'pay.refundsHint': '返金・チャージバック',
  'pay.fee': '{product} の手数料',
  'pay.feeHint': '見込み・純売上の{pct}',
  'pay.net': '受取額',
  'pay.netHint': '見込み・Stripeの決済手数料を引く前',
  'pay.daily': '日別の売上',
  'pay.dailyRange': '直近30日・UTC・{currency}の総売上',
  'pay.dailyLabel': '{currency}の日別総売上（直近30日）',
  'pay.notice.noneTitle': 'このサーバーでは決済が設定されていません。',
  'pay.notice.noneBody': 'サーバーに{keyVar}とWebhookシークレットが設定されるまで、チェックアウトは{code}を返します。{product}サーバーの管理者に設定を依頼してください。',
  'pay.notice.fakeTitle': '組み込みの疑似決済です。お金は動きません。',
  'pay.notice.fakeBody':
    'このサーバーには{env}用のStripeキーがないため、利用者には{product}の「支払う／キャンセル」テスト画面が表示されます。付与・返金・ここの数値は、Stripe利用時とまったく同じように動きます。切り替えるにはサーバーに{keyVar}を設定してください。',
  'pay.notice.testTitle': 'Stripeテストモードです。実際の請求は発生しません。',
  'pay.notice.testBody': 'Stripeのテストカード（例: {card}、有効期限は未来の日付、CVCは任意）で支払えます。本番環境ではライブモードと登録済みの受取口座を使います。',
  'pay.liveBlockedTitle': '登録が完了するまで、本番の購入はブロックされます。',
  'pay.liveBlockedBody': 'それまでチェックアウトは{code}を返します。ストアを非表示にするか「近日公開」と表示してください。',
  'pay.recent': '最近の購入',
  'pay.recentRange': '最新50件・すべての状態',
  'pay.noCheckouts': '{env}ではまだチェックアウトが開始されていません。',
  'pay.col.purchase': '購入',
  'pay.col.player': '利用者',
  'pay.col.amount': '金額',
  'pay.col.status': '状態',
  'pay.col.when': '日時',
  'pay.col.actions': '操作',
  'pay.openPlayer': '利用者を開く',
  'pay.deletedPlayer': '削除済みの利用者',
  'pay.status.completed': '支払い済み',
  'pay.status.pending': '保留中',
  'pay.status.refunded': '返金済み',
  'pay.status.disputed': '異議申し立て',
  'pay.status.failed': '失敗',
  'pay.status.expired': '期限切れ',
  'pay.status.canceled': 'キャンセル',
  'pay.refund': '返金',
  'pay.refundTitle': '{amount}を返金しますか？',
  'pay.refundConfirm': '全額返金',
  'pay.boughtBy': '{who}が{ago}に購入した{title}です。',
  'pay.refundLive': '全額がStripe経由でカードに返金され、あなたの受取額も差し戻されます（{product} は手数料を取らないので、戻す手数料はありません）。',
  'pay.refundTest': 'Stripeテストモードで返金します。実際のお金は動きません。',
  'pay.refundFake': '疑似決済での購入です。お金は動きません。',
  'pay.refundGrants': '購入で付与したものはすべて利用者から取り除かれます（使用済みの通貨は0で止まります）。この操作は取り消せません。',
  'pay.typeToConfirm': '確認のため{key}と入力してください',
  'pay.refunded': '{title}の{amount}を返金しました。',
  'pay.removed': '{who}から{items}を取り除きました。',
  'pay.nothingRemoved': '{who}から取り除くものはありませんでした。',
  'pay.listSep': '、',

  // ------------------------------------------------------------ audit
  'audit.title': '操作の記録',
  'audit.filter': '表示するアプリ',
  'audit.all': 'すべて',
  'audit.current': '{env}（表示中）',
  'audit.emptyTitle': '記録はまだありません',
  'audit.emptyBody': '設定の変更、キーの変更、公開、利用停止がここに記録されます。',
  'audit.loadOlder': 'さらに古い記録を読み込む',
  'audit.secretKey': 'サーバーだけで使うキー{id}',
  'audit.previousOwner': '前の持ち主',
  // 「開発者」は平易な言葉の決まりに当たる（en.ts の同じ行の WHY）。英語の「Another account」と同じ意味
  'audit.otherDeveloper': '別のアカウント',
  'audit.projectCreate': 'プロジェクト{slug}を作成',
  'audit.projectDelete': 'プロジェクトを削除',
  'audit.keyCreate': '{kind}を作成',
  'audit.keyRevoke': 'キーを使えなくした',
  'audit.configPush': '設定ファイルv{version}を反映',
  'audit.playerBan': '利用者{id}を利用停止',
  'audit.playerBanReason': '利用者{id}を利用停止 —「{reason}」',
  'audit.deploymentCreate': 'v{version}をアップロード',
  'audit.deploymentFinalize': 'v{version}の準備が完了',
  'audit.deploymentActivate': 'v{version}を公開',
  'audit.deploymentRollback': 'v{previous}からv{version}に戻した',
  'audit.paymentsRefund': '{amount}を返金',
  'audit.paymentsConnect': '受取口座を登録',
  'audit.playerUnban': '利用者{id}の利用停止を解除',
  // 2026-10-05 console/followups: サーバーが書く種類を全部（auditWords.ts の表・auditWords.test.ts がサーバーのコードと突き合わせる）
  'audit.projectRename': 'プロジェクトの名前を「{name}」に変更',
  // プロジェクトの受け渡し（2026-10-21 core/handoff-1a・auditWords.ts）
  'audit.transferInvited': '{email} さんをプロジェクトの新しい持ち主に招いた',
  'audit.transferCancelled': '受け渡しの招きを取り消した',
  'audit.transferDeclined': '招いた相手が受け渡しを断った',
  'audit.transferAccepted': 'プロジェクトを新しい持ち主に渡した',
  'audit.owner': '持ち主',
  'audit.collaborator': '共同作業者',
  'audit.memberInvited': '{email} さんを共同作業者に加えた',
  'audit.memberRemoved': '共同作業者 {email} さんを外した',
  'audit.memberLeft': '共同作業者が手伝いをやめた',
  'audit.slugChange': 'Webアドレスの名前を{from}から{to}に変更',
  'audit.slugRedirectOn': '前のアドレスの名前{slug}をここへ転送するようにした',
  'audit.slugRedirectOff': '前のアドレスの名前{slug}の転送をやめた',
  'audit.keyRotateAll': 'サーバーだけで使うキーをすべて取り替えた',
  'audit.authProviderUpdate': '{provider}でのログインの設定を変更',
  'audit.authProviderDelete': '{provider}でのログインの設定を削除',
  'audit.playerDelete': '利用者{id}のデータを削除',
  'audit.playerExport': '利用者{id}のデータをダウンロード',
  'audit.playerAge': '利用者{id}の年齢の区分を設定',
  'audit.playerSanction': '利用者{id}を制限',
  'audit.playerSanctionRevoke': '利用者{id}の制限を取り消した',
  'audit.playerSanctionLift': '利用者{id}の制限の期限が終わった',
  'audit.appealAccepted': '利用者{id}の異議を受け入れた',
  'audit.appealDeclined': '利用者{id}の異議を受け入れなかった',
  'audit.paymentBlock': '利用者{id}の支払いを止めた',
  'audit.paymentUnblock': '利用者{id}の支払いを再開',
  'audit.chatHide': 'メッセージを非表示にした',
  'audit.chatUnhide': '非表示のメッセージを表示に戻した',
  'audit.deploymentCancel': 'v{version}のアップロードを取り消した',
  'audit.blobAdopt': 'このプロジェクトにあるファイル{count}個（Blender や AI の 3D で作った物）を公開に使えるようにした',
  'audit.uploadSessionCreate': 'AI のコンピューターからファイルを受け取る準備をした',
  'audit.uploadSessionClaim': 'AI のコンピューターから v{version} を受け取った',
  'audit.servingSuspend': '運営がサイトの公開を止めた',
  'audit.servingResume': '運営がサイトの公開を再開',
  'audit.hostingPromote': '運営が公開中のアプリのファイルを速く届ける配信をオンにした',
  'audit.hostingDemotePause': '運営が公開中のアプリのファイルを速く届ける配信を一時的に止めた（アプリは今までどおり動きます）',
  'audit.hostingDemote': '運営が公開中のアプリのファイルを速く届ける配信をオフにした（アプリは今までどおり動きます）',
  'audit.hostingPromoteOffAll': '運営が公開中のすべてのアプリのファイルを速く届ける配信をまとめて一時的に止めた。このアプリも含まれます（アプリは今までどおり動きます）',
  'audit.functionsEnable': 'Functionsをオンにした',
  'audit.functionsDisable': 'Functionsをオフにした',
  'audit.functionsLimits': '1回の呼び出しあたりの上限を変更',
  'audit.functionsDeploy': 'サーバーv{version}を公開',
  'audit.functionsDelete': 'サーバーを削除',
  'audit.functionsSuspend': 'サーバーが一時停止された',
  'audit.functionsResume': 'サーバーが再び動き始めた',
  'audit.secretPut': '秘密の値{name}を保存',
  'audit.secretDelete': '秘密の値{name}を削除',
  'audit.functionsSweep': 'プロジェクトの削除後にサーバーを片付けた',
  'audit.domainAdd': 'ドメイン{host}を追加',
  'audit.domainRemove': 'ドメイン{host}を削除',
  'audit.domainVerified': 'ドメイン{host}が使えるようになった',
  'audit.domainDeactivated': 'ドメイン{host}が使えなくなった',
  'audit.domainReleased': 'ほかのアカウントが持ち主だと証明したため、ドメイン{host}を手放した',
  'audit.shareLinkCreate': '共有リンクを作成',
  'audit.delegationRevoke': 'AIアプリの接続を取り消した',
  'audit.delegationReplaced': 'AIアプリをつなぎ直したので、前の接続を取り消した',
  'audit.storeCredentialsPut': '{store}の接続情報を保存',
  'audit.storeCredentialsDelete': '{store}の接続情報を削除',
  'audit.storeNoticesPut': 'Google Playの通知の設定を保存',
  'audit.storeNoticesDelete': 'Google Playの通知の設定を削除',
  'audit.giftCreate': '利用者にアイテムを配った',
  'audit.giftCancel': '利用者へのアイテムの配布を取りやめた',
  'audit.serialCreate': '引き換えコードを{count}個作成',
  'audit.serialDisable': '引き換えコードを使えなくした',
  'audit.mailSender': 'メールの送信元の設定を変更',
  'audit.mailCreate': '利用者へのメールを下書き',
  'audit.mailSend': '{count}人の利用者にメールを送った',
  'audit.mailCancel': '利用者へのメールを取りやめた',
  'audit.voiceSuspend': '運営がルーム{room}の音声を止めた',
  'audit.voiceResume': '運営がルーム{room}の音声を再開',
  'audit.contentPurge': '運営が報告された内容を削除',
  'audit.reportResolve': '運営が利用者からの報告への対応を終えた',
  'audit.adsSuspend': '運営が広告を止めた',
  'audit.adsResume': '運営が広告を再開',
  'audit.adsTxt': 'ads.txtのファイルを更新',
  'audit.adNetworkLink': '広告のネットワークに参加',
  'audit.adNetworkUnlink': '広告のネットワークから抜けた',
  'audit.adsImport': '広告の収益を取り込んだ',

  // ------------------------------------------------------------ identity（0013: 復旧・メール確認・サインイン方法）
  'auth.forgotLink': 'パスワードをお忘れですか？',
  'recover.forgotTitle': 'パスワードの再設定',
  'recover.forgotLead': 'アカウントのメールアドレスを入れてください。そのアドレスのアカウントがあれば、新しいパスワードを決めるためのリンクをメールで送ります。',
  'recover.send': 'リンクをメールで送る',
  'recover.sent': '{email}のアカウントがあれば、新しいパスワードを決めるリンクを送りました。リンクは30分で使えなくなります。',
  'recover.devOutbox': '{product}を手元で動かしている場合、メールは送信されません。{link}でリンクを確認できます。',
  'recover.backToLogin': 'ログインに戻る',
  'recover.requestNew': '新しいリンクを送ってもらう',
  'recover.resetTitle': '新しいパスワードの設定',
  'recover.resetLead': '保存すると、念のため、すべての端末のダッシュボードとターミナルのコマンドからログアウトします。新しいパスワードでもう一度ログインしてください。',
  'recover.newPassword': '新しいパスワード',
  'recover.account': 'アカウント',
  'recover.resetFor': '{email} の新しいパスワード',
  'recover.confirmPassword': '新しいパスワード（確認）',
  'recover.mismatch': '2つのパスワードが一致しません。',
  'recover.save': '新しいパスワードを保存',
  'recover.resetDone': 'パスワードを変更しました。新しいパスワードでログインしてください。',
  'recover.missingToken': 'リンクが途中で切れています。メールのリンクを開き直すか、新しいリンクを送ってもらってください。',
  'recover.verifyTitle': 'メールアドレスを確かめる',
  'recover.verifying': '確かめています…',
  'recover.verified': 'メールアドレスを確かめました。',
  'recover.continue': 'ダッシュボードへ進む',
  'verifyBanner.text': 'メールアドレス（{email}）があなたの物か、まだ確かめられていません。届いたメールのリンクを開いてください。開くまで、サーバーだけで使うキー（sk_）を作る・AIエージェントをつなぐ・ログイン方法を足す・出金の設定（受け取りの口座・パスキー）・前払いの入金と自動チャージはできません。',
  'verifyBanner.textLaunch': 'メールアドレス（{email}）があなたの物か、まだ確かめられていません。届いたメールのリンクを開いてください。開くまで、サーバーだけで使うキー（sk_）を作る・AIエージェントをつなぐ・ログイン方法を足す・前払いの入金と自動チャージはできません。',
  'verifyBanner.resend': '確かめるメールをもう一度送る',
  'verifyBanner.sent': '送りました。受信箱（見つからなければ迷惑メールのフォルダも）を見てください。',
  'error.invalid_token': 'このリンクは使えません（期限が切れたか、もう使われています）。新しいリンクを送ってもらってください。',
  'error.redirect_not_allowed': 'このURLは、戻り先URLに登録されていません。',
  'error.secrets_not_configured': 'Googleなどでのログインの設定を、まだ保存できません。サポートに連絡してください。',
  'error.provider_secret_unreadable': '保存してあったログインの設定（Googleなどのサイトでもらった値）が読めなくなりました。お手数ですが、もう一度入れ直してください。',

  'nav.signIn': 'サインイン方法',
  'signin.title': 'サインイン方法',
  'signin.lead':
    '利用者がGoogle・Discord・Apple・Xでログインできるようにし、ログイン後やメールのリンクから戻れるURLを決めます。Googleなどのサイトで作る「アプリ」は、テスト用のアプリ用と、公開中のアプリ用で分けてください。',
  'signin.secretsMissing': 'このサーバーはまだOAuthのクライアントシークレットを保存できる設定になっていません。サーバーの管理者に設定を依頼してください。',
  'signin.redirectTitle': '戻り先として許可するURL',
  'signin.redirectHelp':
    '1行に1つ。URLは完全一致（クエリは自由）で、末尾を*にするとその下のすべてのパスを許可します。Google・Discord・Appleからの戻りと、確認メール・パスワード再設定メールのリンクに使います。',
  'signin.redirectAuto.development': 'テスト用のアプリでは、localhostと127.0.0.1（ポートは任意）と、{product}で公開したこのプロジェクトは、登録しなくても許可されます。',
  'signin.redirectAuto.production': '{product}で公開したこのプロジェクトは、登録しなくても許可されます。公開中のアプリでは、localhostは許可されません。',
  'signin.redirectPlaceholder': 'https://mygame.example.com/*',
  'signin.saveUrls': 'URLを保存',
  'signin.saved': '保存しました。',
  'signin.enabled': '有効',
  'signin.disabled': '無効',
  'signin.configured': '資格情報あり',
  'signin.notConfigured': '資格情報なし',
  'signin.enable': '{provider}でのサインインを許可する',
  'signin.deck.title': '{product}',
  'signin.deck.enable': '{product}でのサインインを許可する',
  'signin.deck.help': '設定は要りません（アプリの登録も鍵も不要）。利用者は 1 つの {product} アカウント（メールのリンク・Google・Discord・X）でいろいろなアプリに入れます。あなたのアプリに渡るのは、このアプリ専用の ID と表示名だけで、メールアドレスは渡りません。',
  'signin.enableNeedsCreds': '下の資格情報を保存してください。保存するまでサインインは拒否されます。',
  'signin.help.google': 'Google Cloud Consoleの「APIとサービス」→「認証情報」で、種類「ウェブアプリケーション」のOAuthクライアントIDを作成します。',
  'signin.help.discord': 'Discord Developer Portalでアプリケーションを作成し、OAuth2を開きます。',
  'signin.help.apple': 'Apple Developerの「Certificates, IDs & Profiles」で、Services IDと、Sign in with Appleを有効にしたキーを作成します。',
  'signin.help.x': 'X Developer Portalで自分のアプリを作成し（種類はWeb App・OAuth 2.0 + PKCE）、下のリダイレクトURIを登録して、OAuth 2.0のClient IDとClient Secretを貼り付けます。アプリ名・アイコンに「X」「Twitter」を入れないでください。Xの同意画面にはあなたのアプリ名が出て、X APIの料金はXからあなたに直接請求されます。',
  'signin.x.choice': '使うXアプリ',
  'signin.x.shared': '設定なしで使う（Xの料金なし）',
  'signin.x.own': '自分のXアプリを使う',
  'signin.x.sharedHelp': 'Xアプリの準備は不要で、Xのログインの料金もかかりません（{product}の利用料はいつもどおり前払いの残高から原価で差し引きます）。同意画面にはアプリの名前ではなく共用の中立な名前が出ます。',
  'signin.x.sharedSwitch': '共用のアプリに切り替えるには、保存したXの資格情報を削除してください。',
  'signin.x.removeBodyShared': '{env}の利用者は、あなたのアプリではなく共用のアプリでXにサインインするようになります。アカウントと進捗は残ります。',
  'signin.callback': '{provider}に登録するリダイレクトURI',
  'signin.clientId': 'クライアントID',
  'signin.servicesId': 'Services ID',
  'signin.clientSecret': 'クライアントシークレット',
  'signin.secretKeep': '空欄のままなら保存済みのシークレット（{hint}）を使い続けます。',
  'signin.teamId': 'Team ID',
  'signin.keyId': 'Key ID',
  'signin.privateKey': '秘密鍵（.p8ファイルの中身）',
  'signin.privateKeyKeep': '空欄のままなら保存済みの鍵を使い続けます。',
  'signin.saveCreds': '資格情報を保存',
  'signin.remove': '資格情報を削除',
  'signin.removeTitle': '{provider}の資格情報を削除しますか？',
  'signin.removeBody': '資格情報を登録し直すまで、{env}では{provider}でサインインできなくなります。利用者のアカウントと進捗は残ります。',
  'signin.sdkTitle': 'アプリから呼ぶ',
  'signin.sdkComment': 'クリックの処理の中で呼ぶ（ポップアップが開きます）',

  'prepaidBand.start': 'はじめに前払いで入金してください。無料の分はなく、使った分の費用をこの残高から引きます（上乗せはしません）。入金するまで、公開したアプリ・サーバー・マルチプレイは使う人から見て止まったままで、新しいプロジェクトを作ることも公開することもできません。',
  'prepaidBand.owed': '公開したアプリ・サーバー・マルチプレイが、使う人から見て止まっています。新しいプロジェクトを作ることも公開することもできません。前払いの残高を使い切り（足りなかった分{owed}）、売上から充てる設定も自動チャージも効いていないためです。入金すると、すべて自動で動き出します。',
  'prepaidBand.empty': '公開したアプリ・サーバー・マルチプレイが、使う人から見て止まっています。新しいプロジェクトを作ることも公開することもできません。前払いの残高を使い切り、売上から充てる設定も自動チャージも効いていないためです。入金すると、すべて自動で動き出します。',
  'prepaidBand.owedLaunch': '公開したアプリ・サーバー・マルチプレイが、使う人から見て止まっています。新しいプロジェクトを作ることも公開することもできません。前払いの残高を使い切り（足りなかった分{owed}）、自動チャージもオフか失敗しているためです。入金すると、すべて自動で動き出します。',
  'prepaidBand.emptyLaunch': '公開したアプリ・サーバー・マルチプレイが、使う人から見て止まっています。新しいプロジェクトを作ることも公開することもできません。前払いの残高を使い切り、自動チャージもオフか失敗しているためです。入金すると、すべて自動で動き出します。',
  'prepaidBand.startUnverified': 'はじめに、上のメールのリンクを開いてメールアドレスを確かめてください。そのあと前払いで入金すると、プロジェクトを作って公開できます。無料の分はありません。',
  'prepaidBand.invitePending': '届いたメールのリンクを開いて、メールアドレスを確かめてください。確かめると{amount}の招待クレジットが入り、入金しなくても使い始められます。',
  'prepaidBand.low': '前払いの残高が少なくなっています（残り{balance}）。なくなるとアプリが止まるので、入金してください。',
  'prepaidBand.lowInvite': '招待クレジットの残りがわずかです（残り{balance}）。続けて使うには入金してください。',
  'prepaidBand.link': '入金する'
};
